'use client'

import { ColorCode, Icon } from '@vinted/web-ui'
import { ArrowRight16 } from '@vinted/monochrome-icons'
import classNames from 'classnames'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import {
  NewAgainPotentialSectionCardModel,
  NewAgainPotentialSectionModel,
} from '@marketplace-web/marketing-campaigns/marketing-landing-page-data'

import useSlider from '../../hooks/useSlider'
import NewAgainPotentialCard from './NewAgainPotentialCard'

type Props = { data: NewAgainPotentialSectionModel }

export const NewAgainPotentialSection = ({ data: { title, cards } }: Props) => {
  const {
    itemRefs,
    handleOnTouchStart,
    handleOnTouchMove,
    handleOnTouchEnd,
    goToNextItem,
    goToPreviousItem,
    wrapperDivRef,
    showButtons,
    currentCardIndex,
  } = useSlider({ childData: cards })

  const breakpoints = useBreakpoint()

  const renderButtonsGroup = (cardIndex: number) => {
    const previousButtonIsDisabled = cardIndex === 0
    const nextButtonIsDisabled = cardIndex === cards.length - 1

    return (
      <div className="new-again__potential-slider-buttons-container">
        <button
          type="button"
          className={classNames('new-again__slider-wrapper-button new-again__rotate-180', {
            disabled: previousButtonIsDisabled,
          })}
          onClick={goToPreviousItem}
          data-testid="slider-previous"
          disabled={previousButtonIsDisabled}
        >
          <Icon
            name={ArrowRight16}
            color={previousButtonIsDisabled ? ColorCode.GreyscaleLevel5 : ColorCode.GreyscaleLevel1}
          />
        </button>
        <button
          type="button"
          className={classNames('new-again__slider-wrapper-button', {
            disabled: nextButtonIsDisabled,
          })}
          onClick={goToNextItem}
          data-testid="slider-next"
          disabled={nextButtonIsDisabled}
        >
          <Icon
            name={ArrowRight16}
            color={nextButtonIsDisabled ? ColorCode.GreyscaleLevel4 : ColorCode.GreyscaleLevel1}
          />
        </button>
      </div>
    )
  }

  const renderSlider = (data: Array<NewAgainPotentialSectionCardModel>) => {
    return (
      <div
        className="new-again__potential-section-slider-container"
        ref={wrapperDivRef}
        onTouchMove={handleOnTouchMove}
        onTouchStart={handleOnTouchStart}
        onTouchEnd={showButtons && breakpoints.wide ? undefined : handleOnTouchEnd}
      >
        {data.map((props, index) => (
          <div ref={itemRefs[index]} className="new-again__slider-wrapper-item" key={index}>
            <NewAgainPotentialCard key={index} {...props} index={index} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <section data-testid="new-again-section" className="new-again__potential-section">
      <div className="new-again__potential-section-inner">
        <div className="new-again__potential-content">
          <h2 className="new-again__potential-title">{title}</h2>
          {showButtons && renderButtonsGroup(currentCardIndex)}
        </div>
        <div className="new-again__potential-cards-wrapper">{renderSlider(cards)}</div>
      </div>
    </section>
  )
}
